import React, { useState } from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "main/javascripts/components/atoms/Icon";
import { IIconFontStyle } from "main/javascripts/styles/fontStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { OptionPickerOption } from "../../types/optionPickerOption";

export interface IProps {
  onSelect(option: OptionPickerOption): void;
  options: OptionPickerOption[];
  maxInitialOptions?: number;
}

export const DropdownOptionPickerOptionList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { options, onSelect, maxInitialOptions = 5 } = props;
  const { t } = useTranslation(["button"]);

  const [isExpanded, setIsExpanded] = useState(false);

  const onOptionSelect = (option: OptionPickerOption): void => {
    onSelect(option);
  };

  const renderOption: (option: any) => React.ReactElement = (option) => {
    const iconType: keyof IIconFontStyle = "search";
    const onOptionClick = () => {
      onOptionSelect(option);
    };

    return (
      <div css={optionStyle} onClick={onOptionClick}>
        <div css={iconStyle}>
          <Icon styleKey={iconType} />
        </div>
        <div css={optionValueBlockStyle}>
          <div css={optionValueStyle}>{option.name}</div>
          {option.description && (
            <div css={optionDescriptionStyle}>{option.description}</div>
          )}
        </div>
      </div>
    );
  };

  const onExpandButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      {options.map((option, index) => {
        if (!isExpanded && index >= maxInitialOptions) {
          return null;
        }
        return (
          <div css={optionBlockStyle} key={option.value}>
            {renderOption(option)}
          </div>
        );
      })}
      {!isExpanded && options.length > maxInitialOptions && (
        <div>
          <div css={expandButtonStyle} onClick={onExpandButtonClick}>
            {t("common.showAll")}
          </div>
        </div>
      )}
    </div>
  );
};

const optionBlockStyle = css`
  border-bottom: 1px solid ${borderColor.primaryLightColor};
`;
const optionStyle = css`
  position: relative;
  margin: 0;
  padding: ${space.atom2x} ${space.atom2x} ${space.atom2x} 3rem;
  cursor: pointer;
  color: ${textColor.primaryDarkColor};
  &:hover {
    background-color: ${backgroundColor.secondaryLightColor};
  }
`;
const iconStyle = css`
  position: absolute;
  top: 50%;
  left: 0.75rem;
  font-size: 1.375rem;
  vertical-align: middle;
  color: ${textColor.secondaryDarkColor};
  opacity: 0.4;
  transform: translateY(-50%);
  display: flex;
`;
const optionValueBlockStyle = css`
  display: flex;
`;
const optionValueStyle = css`
  flex: 1;
  width: 60%;
  font-size: ${fontSize.body};
`;
const optionDescriptionStyle = css`
  display: block;
  width: 40%;
  padding: 0.2rem 0 0 ${space.atom};
  font-size: ${fontSize.caption};
  color: ${textColor.disabledColor};
  line-height: ${lineHeight.caption3};
  text-align: right;
  box-sizing: border-box;
`;
const expandButtonStyle = css`
  cursor: pointer;
  color: ${accentColor.primaryColor};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  padding: ${space.atom1_5x} ${space.atom} ${space.atom2x};
  text-decoration: underline;
  text-underline-offset: ${space.atom};
`;
